import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  Pressable,
  TouchableOpacity,
  ScrollView,
  Platform,
  TextInput,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { AdminColors } from '../Admin/constants/adminColors';
import FontFamily from '../../constants/fonts';
import { QueueItem } from './types';
import { DocumentDateInput } from './EmployeeProfile/components/OnboardingDocument';

export interface LeaderOption {
  id: string;
  name: string;
  gpid?: string;
  shift?: string;
  displayText?: string;
}

interface SetCoverageModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (data: {
    coverFrom: string;
    coverTo: string;
    coveredBy: string;
    coveredByName: string;
    taskIds: string[];
  }) => void | Promise<void>;
  queueItems: QueueItem[];
  leaders: LeaderOption[];
  isSubmitting?: boolean;
}

const formatDateToIso = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Searchable Leader Dropdown attached directly beneath the search input */
interface AttachedSearchableDropdownProps {
  label: string;
  placeholder?: string;
  options: LeaderOption[];
  selectedValue: LeaderOption | null;
  onSelect: (option: LeaderOption) => void;
  disabled?: boolean;
}

const AttachedSearchableDropdown: React.FC<AttachedSearchableDropdownProps> = ({
  label,
  placeholder = 'Select a leader',
  options,
  selectedValue,
  onSelect,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const containerRef = useRef<any>(null);

  const filteredOptions = useMemo(() => {
    if (!searchText.trim()) return options;
    const q = searchText.toLowerCase().trim();
    return options.filter((o) => {
      const display = (o.displayText || o.name || '').toLowerCase();
      return display.includes(q);
    });
  }, [options, searchText]);

  useEffect(() => {
    if (!isOpen) {
      setSearchText('');
    }
  }, [isOpen]);

  // Click outside listener for web
  useEffect(() => {
    if (Platform.OS === 'web' && isOpen) {
      const handleClickOutside = (e: any) => {
        const domNode = containerRef.current;
        if (domNode && typeof domNode.contains === 'function') {
          if (domNode.contains(e.target)) {
            return;
          }
        }
        setIsOpen(false);
        setIsFocused(false);
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const displayLabel = useMemo(() => {
    if (!selectedValue) return '';
    return (
      selectedValue.displayText ||
      (selectedValue.shift ? `${selectedValue.name} (${selectedValue.shift} shift)` : selectedValue.name) ||
      ''
    );
  }, [selectedValue]);

  const handleSelectOption = (opt: LeaderOption) => {
    onSelect(opt);
    setSearchText('');
    setIsOpen(false);
    setIsFocused(false);
  };

  return (
    <View
      ref={containerRef}
      style={[styles.dropdownContainer, { zIndex: isOpen ? 9999 : 1 }]}
    >
      <Text style={styles.fieldLabel}>{label}</Text>

      {/* Search Input / Trigger Box */}
      <View
        style={[
          styles.triggerBox,
          (isOpen || isFocused) && styles.triggerBoxOpen,
          disabled && styles.triggerBoxDisabled,
        ]}
      >
        <TextInput
          style={[
            styles.searchInputField,
            Platform.OS === 'web'
              ? ({
                  outline: 'none',
                  outlineWidth: 0,
                  outlineStyle: 'none',
                  boxShadow: 'none',
                } as any)
              : null,
          ]}
          placeholder={isOpen ? (displayLabel ? `Filter (${displayLabel})` : 'Search leader...') : displayLabel || placeholder}
          placeholderTextColor={!isOpen && displayLabel ? AdminColors.textPrimary : AdminColors.textMuted}
          value={isOpen ? searchText : displayLabel}
          onChangeText={(txt) => {
            setSearchText(txt);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => {
            if (!disabled) {
              setIsFocused(true);
              setIsOpen(true);
            }
          }}
          onBlur={() => {
            setIsFocused(false);
          }}
          editable={!disabled}
        />

        <View style={styles.triggerIcons}>
          {isOpen && searchText.length > 0 && (
            <TouchableOpacity
              onPress={() => setSearchText('')}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              style={styles.clearIcon}
            >
              <MaterialIcons name="close" size={16} color={AdminColors.textSecondary} />
            </TouchableOpacity>
          )}
          <TouchableOpacity
            onPress={() => {
              if (!disabled) {
                setIsOpen((prev) => !prev);
              }
            }}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <MaterialIcons
              name={isOpen ? 'arrow-drop-up' : 'arrow-drop-down'}
              size={24}
              color={AdminColors.textSecondary}
            />
          </TouchableOpacity>
        </View>
      </View>

      {/* Attached Dropdown Menu List */}
      {isOpen && (
        <View style={styles.attachedMenu}>
          <ScrollView
            style={styles.attachedMenuList}
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled"
          >
            {filteredOptions.length === 0 ? (
              <View style={styles.emptyOptionItem}>
                <Text style={styles.emptyOptionText}>No leaders found</Text>
              </View>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = Boolean(
                  selectedValue &&
                  (String(selectedValue.id) === String(opt.id) ||
                    (selectedValue.name && opt.name && selectedValue.name.toLowerCase() === opt.name.toLowerCase()))
                );
                const isHovered = hoveredId === opt.id;
                const optText = opt.displayText || (opt.shift ? `${opt.name} (${opt.shift} shift)` : opt.name);
                return (
                  <TouchableOpacity
                    key={opt.id}
                    style={[
                      styles.optionRow,
                      isSelected && styles.optionRowSelected,
                      isHovered && styles.optionRowHovered,
                    ]}
                    onPress={() => handleSelectOption(opt)}
                    {...(Platform.OS === 'web'
                      ? {
                          onMouseDown: (e: any) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSelectOption(opt);
                          },
                          onMouseEnter: () => setHoveredId(opt.id),
                          onMouseLeave: () => setHoveredId(null),
                        }
                      : {})}
                  >
                    <Text
                      style={[
                        styles.optionText,
                        isSelected && styles.optionTextSelected,
                      ]}
                      numberOfLines={1}
                    >
                      {optText}
                    </Text>
                    {isSelected && (
                      <MaterialIcons name="check" size={18} color={AdminColors.blue700} />
                    )}
                  </TouchableOpacity>
                );
              })
            )}
          </ScrollView>
        </View>
      )}
    </View>
  );
};

export const SetCoverageModal: React.FC<SetCoverageModalProps> = ({
  visible,
  onClose,
  onSubmit,
  queueItems,
  leaders,
  isSubmitting = false,
}) => {
  const { height: windowHeight } = useWindowDimensions();

  const today = useMemo(() => formatDateToIso(new Date()), []);
  const defaultToDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return formatDateToIso(d);
  }, []);

  const [coverFrom, setCoverFrom] = useState(today);
  const [coverTo, setCoverTo] = useState(defaultToDate);
  const [selectedLeader, setSelectedLeader] = useState<LeaderOption | null>(null);

  const prevVisibleRef = useRef(false);

  // Initialize/reset form state ONLY when modal transitions from closed (visible=false) to open (visible=true)
  useEffect(() => {
    if (visible) {
      if (!prevVisibleRef.current) {
        setCoverFrom(today);
        setCoverTo(defaultToDate);
        if (leaders.length > 0) {
          setSelectedLeader(leaders[0]);
        } else {
          setSelectedLeader(null);
        }
      }
    } else {
      setSelectedLeader(null);
    }
    prevVisibleRef.current = visible;
  }, [visible, today, defaultToDate, leaders]);

  // Fallback: If modal is open, selectedLeader was empty, and leaders became available
  useEffect(() => {
    if (visible && !selectedLeader && leaders.length > 0) {
      setSelectedLeader(leaders[0]);
    }
  }, [visible, selectedLeader, leaders]);

  // Eligible tasks: pulse checks and observations that are not drafts
  const eligibleTasks = useMemo(() => {
    return queueItems.filter((item) => {
      const isActivity = item.type === 'Pulse Check' || item.type === 'Observation';
      const isNotDraft = item.band !== 'progress';
      if (!isActivity || !isNotDraft) return false;

      if (item.dueDate) {
        return item.dueDate >= coverFrom && item.dueDate <= coverTo;
      }
      return true;
    });
  }, [queueItems, coverFrom, coverTo]);

  const isValid = Boolean(
    selectedLeader &&
    coverFrom &&
    coverTo &&
    coverTo >= coverFrom
  );

  const handleSubmit = () => {
    if (!isValid || isSubmitting || !selectedLeader) return;
    const leaderName = selectedLeader.name ?? selectedLeader.displayText ?? selectedLeader.id;
    const taskIds = eligibleTasks.map((t) => t.id);
    onSubmit({
      coverFrom,
      coverTo,
      coveredBy: selectedLeader.id,
      coveredByName: leaderName,
      taskIds,
    });
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.scrim}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityLabel="Close dialog" />
        <View style={[styles.modal, { maxHeight: windowHeight * 0.9 }]}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>Set coverage</Text>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} accessibilityLabel="Close">
              <MaterialIcons name="close" size={20} color={AdminColors.textSecondary} />
            </TouchableOpacity>
          </View>

          {/* Body */}
          <View style={styles.body}>
            <Text style={styles.sectionCopy}>
              Every open pulse check and observation you hold that is due inside the period is offered to the covering leader as one bundle. Tasks generated to you during the period are offered to them at the next nightly run until the period ends.
            </Text>

            {/* Cover from */}
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>Cover from</Text>
              <DocumentDateInput value={coverFrom} onChange={setCoverFrom} height={38} />
            </View>

            {/* Cover to */}
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>Cover to</Text>
              <DocumentDateInput
                value={coverTo}
                onChange={setCoverTo}
                min={coverFrom}
                height={38}
                hasError={coverTo < coverFrom}
              />
              {coverTo < coverFrom && (
                <Text style={styles.fieldError}>Cover to date cannot be earlier than cover from date.</Text>
              )}
            </View>

            {/* Covered by: Searchable Attached Dropdown */}
            <AttachedSearchableDropdown
              label="Covered by"
              placeholder="Select a leader"
              options={leaders}
              selectedValue={selectedLeader}
              onSelect={(leader) => {
                setSelectedLeader(leader);
              }}
              disabled={leaders.length === 0}
            />

            {/* Note text */}
            <Text style={styles.noteText}>
              {eligibleTasks.length} open {eligibleTasks.length === 1 ? 'task falls' : 'tasks fall'} inside this period.
            </Text>
          </View>

          {/* Footer */}
          <View style={styles.footer}>
            <TouchableOpacity style={styles.cancelBtn} onPress={onClose} disabled={isSubmitting}>
              <Text style={styles.cancelBtnText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.submitBtn, (!isValid || isSubmitting) && styles.submitBtnDisabled]}
              onPress={handleSubmit}
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting ? (
                <ActivityIndicator size="small" color={AdminColors.textOnDark} />
              ) : (
                <Text style={styles.submitBtnText}>
                  {eligibleTasks.length > 0
                    ? `Offer ${eligibleTasks.length} ${eligibleTasks.length === 1 ? 'task' : 'tasks'}`
                    : 'Set coverage'}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  scrim: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  modal: {
    width: '100%',
    maxWidth: 540,
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    overflow: 'visible',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 16,
    elevation: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.borderDefault,
  },
  title: {
    fontFamily: FontFamily.bold,
    fontSize: 16,
    color: AdminColors.textPrimary,
  },
  body: {
    padding: 20,
    gap: 16,
    zIndex: 10,
    overflow: 'visible',
  },
  sectionCopy: {
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textSecondary,
    lineHeight: 19,
  },
  field: {
    gap: 6,
  },
  fieldLabel: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
    color: AdminColors.textSecondary,
  },
  fieldError: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.red500,
    marginTop: 2,
  },
  dropdownContainer: {
    position: 'relative',
    gap: 6,
  },
  triggerBox: {
    height: 38,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    backgroundColor: AdminColors.bgSurface,
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 10,
    paddingRight: 6,
  },
  triggerBoxOpen: {
    borderColor: AdminColors.blue700,
  },
  triggerBoxDisabled: {
    backgroundColor: AdminColors.gray50,
  },
  searchInputField: {
    flex: 1,
    height: '100%',
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: AdminColors.textPrimary,
    padding: 0,
    margin: 0,
    borderWidth: 0,
    backgroundColor: 'transparent',
  },
  triggerIcons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  clearIcon: {
    padding: 2,
  },
  attachedMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 4,
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    maxHeight: 190,
    zIndex: 99999,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.18,
    shadowRadius: 12,
    elevation: 20,
  },
  attachedMenuList: {
    maxHeight: 188,
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: AdminColors.borderDefault,
  },
  optionRowSelected: {
    backgroundColor: AdminColors.blue50,
  },
  optionRowHovered: {
    backgroundColor: AdminColors.gray50,
  },
  optionText: {
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textPrimary,
    flex: 1,
  },
  optionTextSelected: {
    fontFamily: FontFamily.medium,
    color: AdminColors.blue700,
  },
  emptyOptionItem: {
    padding: 14,
    alignItems: 'center',
  },
  emptyOptionText: {
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textMuted,
  },
  noteText: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    color: AdminColors.textMuted,
    marginTop: -4,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderTopWidth: 1,
    borderTopColor: AdminColors.borderDefault,
    backgroundColor: AdminColors.bgSurface,
    zIndex: 1,
  },
  cancelBtn: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    backgroundColor: AdminColors.bgSurface,
  },
  cancelBtnText: {
    fontFamily: FontFamily.medium,
    fontSize: 13,
    color: AdminColors.textPrimary,
  },
  submitBtn: {
    paddingHorizontal: 18,
    paddingVertical: 8,
    borderRadius: 6,
    backgroundColor: AdminColors.blue700,
    minWidth: 110,
    alignItems: 'center',
    justifyContent: 'center',
  },
  submitBtnDisabled: {
    opacity: 0.5,
  },
  submitBtnText: {
    fontFamily: FontFamily.medium,
    fontSize: 13,
    color: AdminColors.textOnDark,
  },
});
